import { useCallback, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

import { useServerExport, type ExportParams } from 'lib/hooks/useServerExport';
import request from 'lib/request';

import {
  autoConfirmAtom,
  autoConfirmLoadableAtom,
  bulkConfirmAtom,
  bulkConfirmLoadableAtom,
  confirmSlotLoadableAtom,
  slotListAtomFamily,
} from './atoms';
import type { SlotInterestRow } from './types';
import { bulkUatForRow, isEligibleForBulkAm } from './utils';
import { useWorkspaceConfirmation } from './workspace-confirmation-context';

/**
 * One-stop façade for the Group C confirmation mutations. Each submit callback
 * adapts its call-site signature and invokes the underlying mutation atom; the
 * atom's writer performs the HTTP call and the post-success invalidation of the
 * slot list + workspace atoms. Reads `workspaceId` from the confirmation
 * context, so it must be called inside `<WorkspaceConfirmationProvider>`.
 *
 * @return {object} Submit callbacks for each mutation plus their loadable states.
 */
export const useConfirmationMutations = () => {
  const { workspaceId } = useWorkspaceConfirmation();

  const confirmLoadable = useAtomValue(confirmSlotLoadableAtom);
  const bulkConfirm = useSetAtom(bulkConfirmAtom);
  const bulkConfirmLoadable = useAtomValue(bulkConfirmLoadableAtom);
  const autoConfirm = useSetAtom(autoConfirmAtom);
  const autoConfirmLoadable = useAtomValue(autoConfirmLoadableAtom);

  // Stable across renders (the atom setters + workspaceId are stable) so
  // consumers can safely memoize/debounce over them. Per-tract single-slot
  // submission lives on the confirmation context (`submitTracts`), not here.
  const submitBulkConfirm = useCallback(
    (rows: SlotInterestRow[], note?: string) =>
      bulkConfirm(
        workspaceId,
        rows.filter(isEligibleForBulkAm).map((row) => ({
          slot_id: row.slotId,
          streams: row.streams,
          // Per-UAT grain (ONCALL-668): narrow the bulk AM confirm to the row's
          // UAT when it resolves to a single one; a multi-UAT (or no-UAT) row
          // stays whole-slot (null), the BE's slot-level bulk pathway.
          unit_allocation_tract_id: bulkUatForRow(row),
        })),
        // Optional supporting note (AM never requires one); blank collapses
        // to undefined so the atom omits it from the request body.
        note?.trim() || undefined
      ),
    [bulkConfirm, workspaceId]
  );
  const submitAutoConfirm = useCallback(
    (thresholdPct: number) => autoConfirm(workspaceId, thresholdPct),
    [autoConfirm, workspaceId]
  );

  return {
    confirmLoadable,
    submitBulkConfirm,
    bulkConfirmLoadable,
    submitAutoConfirm,
    autoConfirmLoadable,
  };
};

/**
 * Wires the slot comparison table's Export action to the BE's async export
 * endpoint via `useServerExport`. Reads the workspace id + current slot-list
 * filters from context + the search atom; returns the preset list the
 * DataTable wrapper's overflow menu consumes plus the status the caller
 * surfaces inline. The BE only honors the five array filters — the global
 * search input is intentionally NOT forwarded.
 *
 * Matches the project's other server-export call-site convention
 * (`useTypeCurveListingExport`, `useExportAries`, `useExportSlotAllocations`).
 *
 * @return {object} `exportPresets` for the DataTable config + `exportStatus`
 *   from `useServerExport`.
 */
export const useSlotsExport = () => {
  const { workspaceId } = useWorkspaceConfirmation();
  const searchResult = useAtomValue(
    slotListAtomFamily({ surface: 'comparison', workspaceId })
  );
  const currentPayload = searchResult.currentPayload;

  const exportFn = useCallback(
    async (params?: ExportParams): Promise<{ exportId: string }> => {
      const format = params?.format === 'xlsx' ? 'xlsx' : 'csv';
      const body = {
        export: {
          format,
          filters: {
            view: currentPayload?.view,
            streams: currentPayload?.streams,
            operators: currentPayload?.operators,
            formations: currentPayload?.formations,
            well_names: currentPayload?.wellNames,
            hole_directions: currentPayload?.holeDirections,
          },
        },
      };
      const response = await request.post<
        { exportId: string },
        { error?: string }
      >(
        `/interest_certification/workspaces/${workspaceId}/exports`,
        body,
        new AbortController()
      );
      if (response.type !== 'success' || !response.data) {
        const message =
          response.type === 'error' && response.data?.error
            ? response.data.error
            : 'Failed to start export';
        throw new Error(message);
      }
      return { exportId: response.data.exportId };
    },
    [workspaceId, currentPayload]
  );

  const { startExport, exportStatus, resetExport } = useServerExport({
    channel: `interest_certification_workspace_${workspaceId}`,
    event: 'interest_certification_workspace_export_complete',
    exportFn,
  });

  const exportPresets = useMemo(
    () => [
      {
        name: 'CSV',
        format: 'csv' as const,
        onExport: () => startExport({ format: 'csv' }),
      },
      {
        name: 'Excel',
        format: 'xlsx' as const,
        onExport: () => startExport({ format: 'xlsx' }),
      },
    ],
    [startExport]
  );

  return { exportPresets, exportStatus, resetExport };
};
